import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import './GrantDocuments.css';
import {
  LETTERS_OF_SUPPORT_DOC_ID,
  ADDITIONAL_DOCUMENTS_DOC_ID,
  JIT_DOCUMENT_ID_BASE,
  HFT_COMPLIANCE_DOC_ID,
  HFT_IRB_CONSENT_DOC_ID,
  COMMERCIALIZATION_HISTORY_DOC_ID
} from '../utils/grantDocumentIds';
import { PROJECT_ROLES, normalizeProjectRole, isSamePerson } from '../utils/keyPersonnel';

// Just-in-Time documents live on their own page but share this application's
// document store, so they must be filtered out of everything here.
const isJITDocumentId = (documentId) => Number(documentId) >= JIT_DOCUMENT_ID_BASE;

// The budget spreadsheet is provided as a template and returned as-is; everything
// else must be a PDF so we don't have to convert it.
const isBudgetDocument = (document) => !!document.isPrimaryBudget;

// Biosketches and Additional Documents render their own multi-file lists; everything
// else is a single-file document shown as one compact row.
const isStandardDocument = (document) =>
  document.id !== 12 && document.id !== ADDITIONAL_DOCUMENTS_DOC_ID;

// Biosketch address defaults are the company's business address (the Grants.gov
// address on the Company Information page), matching the questionnaire's key
// personnel, so the client doesn't retype it for every person.
const toBusinessAddress = (companyInfo) => {
  const ci = companyInfo || {};
  const a = ci.grantsGovAddress || {};
  return {
    street1: a.line1 || ci.businessAddressLine1 || '',
    street2: a.line2 || ci.businessAddressLine2 || '',
    city: a.city || ci.businessAddressCity || '',
    state: a.state || ci.businessAddressState || '',
    zipCode: a.zip || ci.businessAddressZip || ''
  };
};

const EMPTY_BIOSKETCH_FORM = {
  firstName: '',
  middleName: '',
  lastName: '',
  positionTitle: '',
  organizationName: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  country: '',
  zipCode: '',
  phoneNumber: '',
  email: '',
  eraCommonsUsername: '',
  projectRole: '',
};

const buildBlankBiosketchForm = (companyInfo) => ({
  ...EMPTY_BIOSKETCH_FORM,
  organizationName: companyInfo?.companyName || '',
  country: 'UNITED STATES',
  ...toBusinessAddress(companyInfo)
});

const GrantDocuments = ({ userInfo }) => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({});
  const fileInputRefs = useRef({});

  const [biosketches, setBiosketches] = useState([]);
  const [personnelName, setPersonnelName] = useState('');
  const [showBiosketchForm, setShowBiosketchForm] = useState(false);
  const biosketchFileInputRef = useRef(null);
  
  const [savedPersonnel, setSavedPersonnel] = useState([]);
  const [showPersonnelAutofill, setShowPersonnelAutofill] = useState(false);

  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [biosketchUploading, setBiosketchUploading] = useState(false);

  // Set while editing an already-uploaded biosketch; null when adding a new one.
  const [editingBiosketchId, setEditingBiosketchId] = useState(null);

  // Additional Documents supports any number of files, each with a free-text note.
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [additionalDocNotes, setAdditionalDocNotes] = useState('');
  const [additionalDocUploading, setAdditionalDocUploading] = useState(false);
  const additionalDocFileInputRef = useRef(null);
  const [editingAdditionalDocId, setEditingAdditionalDocId] = useState(null);
  const [editingAdditionalDocNotes, setEditingAdditionalDocNotes] = useState('');
  const [savingAdditionalDocNotes, setSavingAdditionalDocNotes] = useState(false);

  // The heading must show the name the client entered during company formation,
  // not the internal/selected company key. Same source GrantSubmission uses.
  const [companyOfficialName, setCompanyOfficialName] = useState('');
  // Full company record, for the business address biosketches default to.
  const [companyInfo, setCompanyInfo] = useState(null);
  const companyName = companyOfficialName || userInfo?.selectedCompany || '';

  // Helper function to check if file is an xlsx file
  const isXlsxFile = (filename) => {
    if (!filename) return false;
    const extension = filename.toLowerCase().split('.').pop();
    return extension === 'xlsx' || extension === 'xls';
  };

  // Function to handle file download
  const handleFileDownload = async (fileUrl, filename) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to direct download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };


  const [biosketchFormData, setBiosketchFormData] = useState(EMPTY_BIOSKETCH_FORM);

  // Optional documents are excluded so the progress bar reflects required paperwork only.
  const OPTIONAL_DOC_IDS = new Set([String(LETTERS_OF_SUPPORT_DOC_ID), String(ADDITIONAL_DOCUMENTS_DOC_ID)]);

  const calculateUploadedDocumentsCount = (uploadStatus) => {
    // Create a Set of document IDs that have been uploaded
    const uniqueDocumentIds = new Set();

    // Add each document ID to the set
    Object.entries(uploadStatus).forEach(([docId, status]) => {
      if (status.uploaded && !OPTIONAL_DOC_IDS.has(String(docId)) && !isJITDocumentId(docId)) {
        uniqueDocumentIds.add(docId);
      }
    });
    
    return uniqueDocumentIds.size;
  };

  const countRequiredDocuments = (applicationData) =>
    getRequiredDocuments(applicationData).filter(doc => !doc.isOptional).length;

  const handleBiosketchFormChange = (e) => {
    const { name, value } = e.target;
    setBiosketchFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Opening the form for a new person pre-fills the organization/address from the
  // Primary performance site; the user can still edit any of it.
  const openNewBiosketchForm = () => {
    setEditingBiosketchId(null);
    setBiosketchFormData(buildBlankBiosketchForm(companyInfo));
    if (biosketchFileInputRef.current) biosketchFileInputRef.current.value = '';
    setShowBiosketchForm(true);
  };

  const openEditBiosketchForm = (biosketch) => {
    setEditingBiosketchId(biosketch.id);
    setBiosketchFormData({
      firstName: biosketch.firstName || '',
      middleName: biosketch.middleName || '',
      lastName: biosketch.lastName || '',
      positionTitle: biosketch.positionTitle || '',
      organizationName: biosketch.organizationName || '',
      street1: biosketch.street1 || '',
      street2: biosketch.street2 || '',
      city: biosketch.city || '',
      state: biosketch.state || '',
      country: biosketch.country || '',
      zipCode: biosketch.zipCode || '',
      phoneNumber: biosketch.phoneNumber || '',
      email: biosketch.email || '',
      eraCommonsUsername: biosketch.eraCommonsUsername || '',
      projectRole: normalizeProjectRole(biosketch.projectRole),
    });
    if (biosketchFileInputRef.current) biosketchFileInputRef.current.value = '';
    setShowBiosketchForm(true);
  };

  const closeBiosketchForm = () => {
    setShowBiosketchForm(false);
    setEditingBiosketchId(null);
    setBiosketchFormData(buildBlankBiosketchForm(companyInfo));
    if (biosketchFileInputRef.current) biosketchFileInputRef.current.value = '';
  };

  // Fields that must be filled in before a biosketch can be saved.
  const REQUIRED_BIOSKETCH_FIELDS = [
    'firstName', 'lastName', 'positionTitle', 'organizationName',
    'street1', 'city', 'state', 'zipCode', 'country',
    'phoneNumber', 'email', 'projectRole',
  ];

  const getMissingBiosketchFields = () =>
    REQUIRED_BIOSKETCH_FIELDS.filter(
      field => !biosketchFormData[field] || String(biosketchFormData[field]).trim() === ''
    );

  // The biosketch and the questionnaire's Senior/Key Personnel entry describe the
  // same person, so every biosketch save is mirrored into the questionnaire (and
  // adds the person there if they weren't listed yet).
  const syncKeyPersonToQuestionnaire = async (match, details) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/questionnaire/key-personnel`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            match,
            addIfMissing: true,
            person: {
              firstName: details.firstName,
              lastName: details.lastName,
              positionTitle: details.positionTitle,
              projectRole: details.projectRole,
              eraCommonsUsername: details.eraCommonsUsername,
              organizationName: details.organizationName
            }
          })
        }
      );
      if (!response.ok) return;
      const data = await response.json();
      setApplication(prev => (prev ? {
        ...prev,
        questionnaire: {
          ...(prev.questionnaire || {}),
          answers: { ...(prev.questionnaire?.answers || {}), keyPersonnel: data.keyPersonnel || [] }
        }
      } : prev));
    } catch (error) {
      console.error('Error syncing key person to questionnaire:', error);
    }
  };

  // Save edits to an existing biosketch's details. The file itself is untouched —
  // to swap the file, remove the biosketch and upload it again.
  const handleBiosketchUpdate = async () => {
    const missing = getMissingBiosketchFields();
    if (missing.length > 0) {
      alert(`Please fill in all required fields: ${missing.join(', ')}`);
      return;
    }

    setBiosketchUploading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/documents/${editingBiosketchId}/metadata`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ metadata: biosketchFormData })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save biosketch details');
      }

      const original = biosketches.find(bs => bs.id === editingBiosketchId) || biosketchFormData;
      await syncKeyPersonToQuestionnaire(
        { firstName: original.firstName, lastName: original.lastName },
        biosketchFormData
      );

      setBiosketches(prev =>
        prev.map(bs => (bs.id === editingBiosketchId ? { ...bs, ...biosketchFormData } : bs))
      );

      closeBiosketchForm();

      // Keep the autofill list in step with the edited details
      setTimeout(() => fetchSavedPersonnel(), 1000);
    } catch (error) {
      console.error('Error updating biosketch:', error);
      alert(`Failed to save biosketch details: ${error.message}`);
    } finally {
      setBiosketchUploading(false);
    }
  };

  // Save an edited note against an already-uploaded Additional Document.
  const handleSaveAdditionalDocNotes = async (docId) => {
    setSavingAdditionalDocNotes(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/documents/${docId}/metadata`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ metadata: { notes: editingAdditionalDocNotes } })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save note');
      }

      setAdditionalDocuments(prev =>
        prev.map(doc => (doc.id === docId ? { ...doc, notes: editingAdditionalDocNotes } : doc))
      );
      setEditingAdditionalDocId(null);
      setEditingAdditionalDocNotes('');
    } catch (error) {
      console.error('Error updating document note:', error);
      alert(`Failed to save note: ${error.message}`);
    } finally {
      setSavingAdditionalDocNotes(false);
    }
  };


  const triggerFileInput = (documentId) => {
    if (fileInputRefs.current[documentId]) {
      fileInputRefs.current[documentId].click();
    }
  };

  // Fetch saved personnel from previous grants
  const fetchSavedPersonnel = async () => {
    try {
      console.log('Fetching saved personnel from:', `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/saved-personnel`);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/saved-personnel`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('Saved personnel response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Saved personnel data:', data);
        const personnel = data.personnel || [];
        console.log('Setting saved personnel:', personnel);
        setSavedPersonnel(personnel);
        
        // No mock data - showing actual backend data only
        console.log('Backend returned', personnel.length, 'saved personnel');
      } else {
        console.error('Failed to fetch saved personnel, status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        // No mock data fallback - showing actual backend response
        console.log('Backend returned error, setting empty array');
        setSavedPersonnel([]);
      }
    } catch (error) {
      console.error('Error fetching saved personnel:', error);
      // No mock data in catch block - showing actual error state
      console.log('Fetch failed, setting empty array');
      setSavedPersonnel([]);
    }
  };

  // Senior/key personnel the questionnaire lists at the client company. Each one
  // needs a biosketch here; people at other organizations are handled on the
  // Sub-Contract Documents Management page instead.
  const clientCompanyKeyPersonnel = (() => {
    const answers = application?.questionnaire?.answers;
    const people = Array.isArray(answers?.keyPersonnel) ? answers.keyPersonnel : [];
    const clientOrgs = new Set(
      [companyName, answers?.projectSites?.[0]?.organizationName]
        .map(name => (name || '').trim())
        .filter(Boolean)
    );
    return people.filter((person, index) => {
      const hasName = (person.firstName || '').trim() || (person.lastName || '').trim();
      if (!hasName) return false;
      // The first key person is always the lead investigator at the company.
      if (index === 0) return true;
      const org = (person.organizationName || '').trim();
      return !org || clientOrgs.size === 0 || clientOrgs.has(org);
    });
  })();

  const biosketchExistsFor = (person) => biosketches.some(bio => isSamePerson(bio, person));

  const keyPersonnelNeedingBiosketch = clientCompanyKeyPersonnel.filter(p => !biosketchExistsFor(p));

  // Start a biosketch from what the questionnaire already knows about a person, so
  // the two pages hold the same details.
  const openBiosketchFormForKeyPerson = (person) => {
    setEditingBiosketchId(null);
    setBiosketchFormData({
      ...buildBlankBiosketchForm(companyInfo),
      firstName: person.firstName || '',
      lastName: person.lastName || '',
      positionTitle: person.positionTitle || '',
      organizationName: companyName || person.organizationName || '',
      country: 'UNITED STATES',
      eraCommonsUsername: person.eraCommonsUsername || '',
      projectRole: normalizeProjectRole(person.projectRole)
    });
    if (biosketchFileInputRef.current) biosketchFileInputRef.current.value = '';
    setShowBiosketchForm(true);
  };

  // Auto-fill personnel data (excluding biosketch)
  const handlePersonnelAutofill = (personnel) => {
    setBiosketchFormData({
      firstName: personnel.firstName,
      middleName: personnel.middleName || '',
      lastName: personnel.lastName,
      positionTitle: personnel.positionTitle,
      organizationName: personnel.organizationName,
      street1: personnel.street1,
      street2: personnel.street2 || '',
      city: personnel.city,
      state: personnel.state,
      country: personnel.country,
      zipCode: personnel.zipCode,
      phoneNumber: personnel.phoneNumber,
      email: personnel.email,
      eraCommonsUsername: personnel.eraCommonsUsername || '',
      projectRole: normalizeProjectRole(personnel.projectRole)
    });
    
    setShowPersonnelAutofill(false);
  };

  // Note: Personnel data is now automatically available for autofill
  // from existing biosketch document submissions - no separate save needed
  
  // Function to generate required documents based on application data
  const getRequiredDocuments = (applicationData) => {
    console.log("getting required docs");
    const baseDocuments = [
    { 
      id: 1, 
      name: 'PHS Assignment Request Form', 
      description: 'Standard form required for NIH grant applications', 
      hasExample: true 
    },
    { 
      id: 2, 
      name: 'Cover Letter', 
      description: 'Formal letter introducing your grant application', 
      hasExample: false 
    },
    { 
      id: 3, 
      name: 'Abstract', 
      description: 'Brief summary of the proposed project (typically 30 lines or less)', 
      hasExample: false 
    },
    { 
      id: 4, 
      name: 'Project Narrative', 
      description: 'Short explanation of the relevance of this research to public health', 
      hasExample: false 
    },
    { 
      id: 5, 
      name: 'References Cited', 
      description: 'Bibliography of all references cited in the application', 
      hasExample: false 
    },
    {
      id: 6,
      name: 'Facilities and Other Resources',
      description: 'Description of facilities and resources available for the project',
      hasExample: false
    },
    {
      id: 7,
      name: 'Major Equipment',
      description: 'List of equipment available for the proposed research',
      hasExample: false
    },
    { 
      id: 8, 
      name: 'Specific Aims', 
      description: 'Concise goals of the proposed research (typically 1 page)', 
      hasExample: false 
    },
    { 
      id: 9, 
      name: 'Research Strategy', 
      description: 'Detailed research plan including significance, innovation, and approach', 
      hasExample: false 
    },
    { 
      id: 10, 
      name: 'Data Management and Sharing Plan', 
      description: 'Plan for managing and sharing research data', 
      hasExample: false 
    },
    { 
      id: 11, 
      name: 'Authentication', 
      description: 'Plan for authenticating key biological and/or chemical resources', 
      hasExample: false 
    },
    { 
      id: 12, 
      name: `Biosketches of all key personnel${companyName ? ` at ${companyName}` : ''}`, 
      description: 'NIH-format biographical sketches for all key personnel', 
      hasExample: false 
    },
  ];

  // Add conditional documents
  let nextDocId = 14;

  console.log("applicationdata", applicationData);
  
  // Add Introduction upload for Resubmission
  if (applicationData?.questionnaire?.answers?.grantApplicationType === 'Resubmission') {
    baseDocuments.push({
      id: nextDocId++,
      name: 'Introduction',
      description: 'Introduction document required for resubmission applications',
      hasExample: false,
      isConditional: true,
      condition: 'resubmission'
    });
  }

  // Add Commercialization Plan upload for DOE Phase I or Phase II/Fast-Track (all agencies)
  const questionnaire = applicationData?.questionnaire?.answers;
  const needsCommercializationPlan = 
    (questionnaire?.applicationType === "Phase II") ||
    (questionnaire?.applicationType === "Fast-Track") ||
    (questionnaire?.applicationType === "Direct Phase II") ||
    (questionnaire?.applicationType === "Phase IIA") ||
    (questionnaire?.applicationType === "Phase IIB") ||
    (questionnaire?.applicationType === "Commercialization Readiness Program") ||
    (questionnaire?.applicationType === "Phase I" && questionnaire?.agency === "DOE");
    
  if (needsCommercializationPlan) {
    baseDocuments.push({
      id: nextDocId++,
      name: 'Commercialization Plan',
      description: 'Required for Phase I (DOE only), Phase II (all agencies), Phase I/II Fast-Track (all agencies)',
      hasExample: false,
      isConditional: true,
      condition: 'commercialization'
    });
  }

  // Human Fetal Tissue paperwork. The questionnaire asks the question; the two
  // forms it calls for are uploaded here.
  if (questionnaire?.humanFetalTissue === 'Yes') {
    baseDocuments.push({
      id: HFT_COMPLIANCE_DOC_ID,
      name: 'HFT Compliance Assurance',
      description: 'Required because the application involves human fetal tissue from elective abortions',
      hasExample: false,
      isConditional: true,
      condition: 'human_fetal_tissue'
    });
    baseDocuments.push({
      id: HFT_IRB_CONSENT_DOC_ID,
      name: 'HFT Sample IRB Consent Form',
      description: 'Required because the application involves human fetal tissue from elective abortions',
      hasExample: false,
      isConditional: true,
      condition: 'human_fetal_tissue'
    });
  }

  // Prior SBIR Phase II awards (SBIR question 9) call for the company's
  // commercialization history.
  if (questionnaire?.receivedSBIRPhaseII === 'Yes') {
    baseDocuments.push({
      id: COMMERCIALIZATION_HISTORY_DOC_ID,
      name: 'Commercialization History',
      description: 'Provide a company commercialization history in accordance with agency-specific instructions using this attachment.',
      note: 'Requested because you answered "Yes" to SBIR question 9 (Have you received SBIR Phase II awards from the Federal Government?) in this application\'s questionnaire. Upload the file here.',
      hasExample: false,
      isConditional: true,
      condition: 'sbir_phase_ii_history'
    });
  }

  // Add budget documents for each performing site
  // Add budget documents only for the primary site (Base Company)
  // Subcontractor documents are now managed in a separate page
  const primarySite = applicationData?.questionnaire?.answers?.projectSites?.[0];
  const siteName = 'Base Company (Primary Site)';

  baseDocuments.push({
    id: nextDocId++,
    name: `Budget - ${siteName}`,
    description: `Detailed budget for ${siteName}`,
    hasExample: true,
    isPerformingSite: true,
    siteIndex: 0,
    isPrimaryBudget: true
  });

  baseDocuments.push({
    id: nextDocId++,
    name: `Budget Justification - ${siteName}`,
    description: `Detailed justification for budget items for ${siteName}`,
    hasExample: false,
    isPerformingSite: true,
    siteIndex: 0,
    isPrimaryBudgetJustification: true
  });

  // Optional documents. These use fixed high IDs (rather than nextDocId) because the
  // conditional documents above shift the sequence depending on the questionnaire,
  // and an uploaded file's ID must stay stable across visits.
  baseDocuments.push({
    id: LETTERS_OF_SUPPORT_DOC_ID,
    name: 'Letters of Support',
    description: 'Please combine all letters of support into a single pdf.',
    hasExample: false,
    isOptional: true
  });

  baseDocuments.push({
    id: ADDITIONAL_DOCUMENTS_DOC_ID,
    name: 'Additional Documents',
    description: 'Anything else you need to send us. Add a note describing each file so we know what it is.',
    hasExample: false,
    isOptional: true,
    isMultiUpload: true
  });

  return baseDocuments;
};

  // Fetch application details and document status
  useEffect(() => {
    const fetchApplicationDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch application details');
        }

        const data = await response.json();
        setApplication(data.application);
        
        // Set initial upload status based on existing documents
        if (data.application.documents) {
          const status = {};
          data.application.documents.forEach(doc => {
            status[doc.documentId] = {
              uploaded: true,
              filename: doc.filename,
              uploadDate: doc.uploadDate,
              fileUrl: doc.fileUrl,
            };
          });
          setUploadStatus(status);

          // Additional Documents can have many files under one document ID, each
          // carrying its own note in metadata.
          const additional = data.application.documents
            .filter(doc => String(doc.documentId) === String(ADDITIONAL_DOCUMENTS_DOC_ID))
            .map(doc => {
              let metadata = {};
              try {
                metadata = doc.metadata
                  ? (typeof doc.metadata === 'string' ? JSON.parse(doc.metadata) : doc.metadata)
                  : {};
              } catch (e) {
                metadata = {};
              }
              return {
                id: doc.id,
                filename: doc.filename,
                uploadDate: doc.uploadDate,
                fileUrl: doc.fileUrl,
                notes: metadata.notes || ''
              };
            });
          setAdditionalDocuments(additional);
        }
      } catch (error) {
        console.error('Error fetching application:', error);
        setError('Failed to load application details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (applicationId) {
      fetchApplicationDetails();
      fetchSavedPersonnel();
    }
  }, [applicationId]);

  // Official company name, as entered by the client on the company formation form.
  useEffect(() => {
    const fetchCompanyOfficialName = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/company-info`,
          { method: 'GET', credentials: 'include' }
        );
        if (!response.ok) return;
        const data = await response.json();
        setCompanyInfo(data.companyInfo || null);
        if (data.companyInfo?.companyName) {
          setCompanyOfficialName(data.companyInfo.companyName);
        }
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };

    fetchCompanyOfficialName();
  }, []);

  // Handle file upload
  const handleFileUpload = async (documentId, file) => {
    try {
      setUploadStatus(prev => ({
        ...prev,
        [documentId]: {
          ...prev[documentId],
          uploading: true,
          error: null
        }
      }));

      // Step 1: Get a signed URL for uploading
      const getUrlResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/document-upload-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentId,
          filename: file.name,
          contentType: file.type
        })
      });

      if (!getUrlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const urlData = await getUrlResponse.json();
      
      // Step 2: Upload the file directly to Google Cloud Storage
      const uploadResponse = await fetch(urlData.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload document to cloud storage');
      }

      // Step 3: Confirm the upload to update our database
      const confirmResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/confirm-document-upload`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentId,
          filename: file.name,
          destination: urlData.destination,
          downloadUrl: urlData.downloadUrl
        })
      });

      if (!confirmResponse.ok) {
        throw new Error('Failed to confirm document upload');
      }

      const confirmData = await confirmResponse.json();

      setUploadStatus(prev => ({
        ...prev,
        [documentId]: {
          uploaded: true,
          filename: file.name,
          uploadDate: new Date().toISOString(),
          uploading: false,
          fileUrl: urlData.downloadUrl
        }
      }));
    } catch (error) {
      console.error('Error uploading document:', error);
      setUploadStatus(prev => ({
        ...prev,
        [documentId]: {
          ...prev[documentId],
          uploading: false,
          error: `Failed to upload: ${error.message}`
        }
      }));
    }
  };

  // Replace or add the handleBiosketchSubmit function
  const handleBiosketchSubmit = async () => {
    // Editing an existing entry only touches the stored details, not the file.
    if (editingBiosketchId) {
      return handleBiosketchUpdate();
    }

    const file = biosketchFileInputRef.current?.files?.[0];
    
    setBiosketchUploading(true);
    setFormValidationErrors({});

    if (getMissingBiosketchFields().length > 0) {
      alert("Missing required fields.");
      setBiosketchUploading(false);
      return;
    }

    if (!file) {
      alert("Please select a biosketch file to upload");
      setBiosketchUploading(false);
      return;
    }

    // Validate file type for biosketch (should be PDF or Word document)
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      alert("Please select a PDF or Word document for the biosketch. Excel files are not appropriate for biosketches.");
      setBiosketchUploading(false);
      return;
    }

    console.log("Uploading biosketch file:", file.name, "Type:", file.type);
    
    if (!biosketchFormData.firstName || !biosketchFormData.lastName) {
      alert("First name and last name are required");
      setBiosketchUploading(false);
      return;
    }

    // Check if this person already has a biosketch being uploaded or uploaded
    const fullName = `${biosketchFormData.firstName} ${biosketchFormData.lastName}`;
    const existingBiosketch = biosketches.find(bio => 
      (bio.firstName === biosketchFormData.firstName && bio.lastName === biosketchFormData.lastName) ||
      bio.personnelName === fullName
    );
    
    if (existingBiosketch) {
      if (existingBiosketch.uploading) {
        alert("A biosketch for this person is already being uploaded. Please wait for it to complete.");
        setBiosketchUploading(false);
        return;
      } else {
        const confirmReplace = window.confirm(`A biosketch for ${fullName} already exists. Do you want to replace it?`);
        if (!confirmReplace) {
          setBiosketchUploading(false);
          return;
        }
        // Remove the existing biosketch
        setBiosketches(prev => prev.filter(bio => bio.id !== existingBiosketch.id));
      }
    }
    
    try {
      // Determine which document ID corresponds to biosketches
      const biosketchDocId = 12;
      
      // Create a safe filename that includes the personnel name and timestamp to avoid conflicts
      const personnelNameForFile = `${biosketchFormData.lastName}_${biosketchFormData.firstName}`;
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const baseName = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
      const enhancedFilename = `Biosketch_${personnelNameForFile.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}.${fileExtension}`;
      
      // Show uploading status with unique ID
      const uniqueId = `temp_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
      setBiosketches(prev => [
        ...prev,
        {
          id: uniqueId, // Unique temporary ID for UI purposes
          ...biosketchFormData,
          filename: enhancedFilename,
          uploading: true,
          error: null
        }
      ]);

      // Step 1: Get a signed URL for uploading
      const getUrlResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/document-upload-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentId: biosketchDocId,
          filename: enhancedFilename,
          contentType: file.type,
          metadata: biosketchFormData
        })
      });

      if (!getUrlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const urlData = await getUrlResponse.json();
      
      // Step 2: Upload the file directly to Google Cloud Storage
      const uploadResponse = await fetch(urlData.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload document to cloud storage');
      }

      // Step 3: Confirm the upload to update our database
      const confirmResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/confirm-document-upload`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentId: biosketchDocId,
          filename: enhancedFilename,
          destination: urlData.destination,
          downloadUrl: urlData.downloadUrl,
          metadata: biosketchFormData
        })
      });

      if (!confirmResponse.ok) {
        throw new Error('Failed to confirm document upload');
      }

      const confirmData = await confirmResponse.json();

      // Update the biosketches list with the uploaded file
      setBiosketches(prev => 
        prev.map(bs => 
          bs.filename === enhancedFilename ? 
          {
            ...bs,
            id: confirmData.document.id, // Replace temporary ID with real one
            uploadDate: confirmData.document.uploadDate,
            fileUrl: urlData.downloadUrl,
            uploading: false
          } : bs
        )
      );

      // Also mark the biosketch document type as uploaded
      setUploadStatus(prev => ({
        ...prev,
        [biosketchDocId]: {
          uploaded: true
        }
      }));

      await syncKeyPersonToQuestionnaire(
        { firstName: biosketchFormData.firstName, lastName: biosketchFormData.lastName },
        biosketchFormData
      );

      // Personnel data is now automatically available for autofill from this biosketch submission
      console.log('Biosketch submitted - refreshing saved personnel for autofill');
      
      // Refetch saved personnel so they're immediately available for autofill
      setTimeout(() => {
        fetchSavedPersonnel();
      }, 1000); // Small delay to ensure the biosketch is fully saved

      // Reset the form
      closeBiosketchForm();
      setBiosketchUploading(false);
    } catch (error) {
      console.error('Error uploading biosketch:', error);
      setBiosketchUploading(false);
      
      // Update error status
      setBiosketches(prev => 
        prev.map(bs => 
          bs.filename?.includes(biosketchFormData.lastName) ? 
          {
            ...bs,
            uploading: false,
            error: `Failed to upload: ${error.message}`
          } : bs
        )
      );
    }
  };

  // Handle file input change
  const handleFileChange = (e, documentId) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(documentId, file);
    }
  };

  // Handle download example
  const handleDownloadExample = async (documentId) => {
    try {
      const exampleResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/example-documents/${documentId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await exampleResponse.json();
      if (data.success && data.url) {
        // Create a temporary link to download the file
        const link = document.createElement('a');
        link.href = data.url;
        link.download = ''; // Let browser determine filename
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Template document not available. Please contact your administrator to upload a template file.');
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template. Please try again or contact support.');
    }
  };

  // Handle batch download of all uploaded documents
  const handleBatchDownload = async () => {
    const uploadedDocuments = Object.entries(uploadStatus)
      .filter(([documentId, status]) => status.uploaded && status.fileUrl && !isJITDocumentId(documentId))
      .map(([documentId, status]) => ({ documentId, fileUrl: status.fileUrl, filename: status.filename }));

    if (uploadedDocuments.length === 0) {
      alert('No documents have been uploaded yet.');
      return;
    }

    try {
      // Create a new JSZip instance
      const zip = new JSZip();
      
      // Add loading indicator
      const button = document.querySelector('.batch-download-btn');
      const originalText = button.textContent;
      button.textContent = 'Creating zip file...';
      button.disabled = true;

      // Fetch each file and add to zip
      for (const doc of uploadedDocuments) {
        try {
          const response = await fetch(doc.fileUrl);
          if (response.ok) {
            const blob = await response.blob();
            const filename = doc.filename || `document_${doc.documentId}`;
            zip.file(filename, blob);
          }
        } catch (error) {
          console.error(`Failed to fetch ${doc.filename}:`, error);
        }
      }

      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `grant_documents_${applicationId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up object URL
      URL.revokeObjectURL(link.href);
      
      // Restore button
      button.textContent = originalText;
      button.disabled = false;
      
    } catch (error) {
      console.error('Error creating zip file:', error);
      alert('Failed to create zip file. Please try again.');
      
      // Restore button on error
      const button = document.querySelector('.batch-download-btn');
      button.textContent = `Download All (${calculateUploadedDocumentsCount(uploadStatus)})`;
      button.disabled = false;
    }
  };

  // Return to applications list
  const handleReturn = () => {
    navigate('/grant-submission');
  };

  const handleBiosketchUpload = async (file, personnelName) => {
    try {
      // Determine which document ID corresponds to biosketches (12 in your case)
      const biosketchDocId = 12;
      
      // Create a filename that includes the personnel name
      const enhancedFilename = `Biosketch_${personnelName.replace(/\s+/g, '_')}_${file.name}`;
      
      // Show uploading status
      setBiosketches(prev => [
        ...prev,
        {
          id: Date.now(), // Temporary ID for UI purposes
          personnelName,
          filename: enhancedFilename,
          uploading: true,
          error: null
        }
      ]);
  
      // Step 1: Get a signed URL for uploading
      const getUrlResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/document-upload-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentId: biosketchDocId,
          filename: enhancedFilename,
          contentType: file.type,
          metadata: {
            personnelName
          }
        })
      });
  
      if (!getUrlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }
  
      const urlData = await getUrlResponse.json();
      
      // Step 2: Upload the file directly to Google Cloud Storage
      const uploadResponse = await fetch(urlData.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });
  
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload document to cloud storage');
      }
  
      // Step 3: Confirm the upload to update our database
      const confirmResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/confirm-document-upload`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentId: biosketchDocId,
          filename: enhancedFilename,
          destination: urlData.destination,
          downloadUrl: urlData.downloadUrl,
          metadata: {
            personnelName
          }
        })
      });
  
      if (!confirmResponse.ok) {
        throw new Error('Failed to confirm document upload');
      }
  
      const confirmData = await confirmResponse.json();
  
      // Update the biosketches list with the uploaded file
      setBiosketches(prev => 
        prev.map(bs => 
          bs.filename === enhancedFilename ? 
          {
            ...bs,
            id: confirmData.document.id, // Replace temporary ID with real one
            uploadDate: confirmData.document.uploadDate,
            fileUrl: urlData.downloadUrl,
            uploading: false
          } : bs
        )
      );
  
      // Also mark the biosketch document type as uploaded
      setUploadStatus(prev => ({
        ...prev,
        [biosketchDocId]: {
          uploaded: true
        }
      }));
  
      // Reset the form
      setPersonnelName('');
      setShowBiosketchForm(false);
    } catch (error) {
      console.error('Error uploading biosketch:', error);
      
      // Update error status
      setBiosketches(prev => 
        prev.map(bs => 
          bs.personnelName === personnelName ? 
          {
            ...bs,
            uploading: false,
            error: `Failed to upload: ${error.message}`
          } : bs
        )
      );
    }
  };
  
  // Add this function to handle biosketch file input change
  const handleBiosketchFileChange = (e) => {
    const file = e.target.files[0];
    if (file && personnelName.trim()) {
      handleBiosketchUpload(file, personnelName.trim());
    } else if (!personnelName.trim()) {
      alert("Please enter a personnel name first");
    }
  };
  
  // Add this to fetch existing biosketches when loading the page
  useEffect(() => {
    const fetchBiosketches = async () => {
      if (!applicationId) return;
      
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/biosketches`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
  
        if (!response.ok) {
          throw new Error('Failed to fetch biosketches');
        }
  
        const data = await response.json();
        if (data.biosketches && data.biosketches.length > 0) {
          setBiosketches(data.biosketches);
          
          // Also mark the biosketch document as uploaded
          setUploadStatus(prev => ({
            ...prev,
            [12]: { uploaded: true }
          }));
        }
      } catch (error) {
        console.error('Error fetching biosketches:', error);
      }
    };
  
    fetchBiosketches();
  }, [applicationId]);
  
  // Upload one Additional Document. Unlike the single-file documents, each upload is
  // kept as its own record (same document ID) along with the note describing it.
  const handleAdditionalDocumentUpload = async (file, notes) => {
    if (!file) return;

    setAdditionalDocUploading(true);
    try {
      const timestamp = new Date().getTime();
      const uniqueFilename = `Additional_${timestamp}_${file.name}`;

      const getUrlResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/document-upload-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId: ADDITIONAL_DOCUMENTS_DOC_ID,
          filename: uniqueFilename,
          contentType: file.type
        })
      });

      if (!getUrlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const urlData = await getUrlResponse.json();

      const uploadResponse = await fetch(urlData.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload document to cloud storage');
      }

      const confirmResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/confirm-document-upload`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId: ADDITIONAL_DOCUMENTS_DOC_ID,
          filename: uniqueFilename,
          destination: urlData.destination,
          downloadUrl: urlData.downloadUrl,
          metadata: { notes: notes || '', originalFilename: file.name }
        })
      });

      if (!confirmResponse.ok) {
        throw new Error('Failed to confirm document upload');
      }

      const confirmData = await confirmResponse.json();

      setAdditionalDocuments(prev => [
        ...prev,
        {
          id: confirmData.document?.id || `${timestamp}`,
          filename: uniqueFilename,
          uploadDate: new Date().toISOString(),
          fileUrl: urlData.downloadUrl,
          notes: notes || ''
        }
      ]);
      setAdditionalDocNotes('');
      if (additionalDocFileInputRef.current) {
        additionalDocFileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading additional document:', error);
      alert(`Failed to upload document: ${error.message}`);
    } finally {
      setAdditionalDocUploading(false);
    }
  };

  const handleRemoveAdditionalDocument = async (documentRecordId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/documents/${documentRecordId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      setAdditionalDocuments(prev => prev.filter(doc => doc.id !== documentRecordId));
    } catch (error) {
      console.error('Error removing additional document:', error);
      alert(`Failed to remove document: ${error.message}`);
    }
  };

  // Function to remove a biosketch
  const handleRemoveBiosketch = async (biosketchId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/documents/${biosketchId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
  
      if (!response.ok) {
        console.error('Failed to delete biosketch:', response);
        throw new Error('Failed to delete biosketch');
      }
  
      // Remove from state
      setBiosketches(prev => prev.filter(bs => bs.id !== biosketchId));
      
      // Update document status if all biosketches are removed
      if (biosketches.length <= 1) {
        setUploadStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[12]; // Remove biosketch document status
          return newStatus;
        });
      }
    } catch (error) {
      console.error('Error removing biosketch:', error);
      alert(`Failed to remove biosketch: ${error.message}`);
    }
  };
  

  if (isLoading) {
    return <div className="loading-indicator">Loading document requirements...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="grant-documents-container">
      <div className="documents-header">
        <h1>Grant Application Documents</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="return-btn"
            onClick={() => navigate(`/subcontractor-documents/${applicationId}`)}
            style={{ backgroundColor: '#10b981' }}
          >
            Manage Subcontractor Documents
          </button>
          <button className="return-btn" onClick={handleReturn}>
            Return to Applications
          </button>
        </div>
      </div>

      <div className="application-info-bar">
        <h2>{application?.name}</h2>
        <div className="document-progress">
          <span>Progress: </span>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${application ? calculateUploadedDocumentsCount(uploadStatus) / countRequiredDocuments(application) * 100 : 0}%` }}
            ></div>
          </div>
          <span className="progress-text">
            {calculateUploadedDocumentsCount(uploadStatus)}/{application ? countRequiredDocuments(application) : 0} Documents Uploaded
          </span>
        </div>
        <div className="batch-download-section">
          <button
            className="batch-download-btn"
            onClick={handleBatchDownload}
            disabled={calculateUploadedDocumentsCount(uploadStatus) === 0}
            title={calculateUploadedDocumentsCount(uploadStatus) === 0 ? 'No documents uploaded yet' : 'Download all uploaded documents'}
          >
            Download All ({calculateUploadedDocumentsCount(uploadStatus)})
          </button>
        </div>
      </div>

      <div className="documents-list">
        {application && getRequiredDocuments(application).map(document => (
          <div key={document.id} className="document-card">
            <div className="document-header">
              <div className="document-title-block">
                <h4>{document.name}</h4>
                {document.note && (
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#92400e' }}>{document.note}</p>
                )}
                {document.note && document.description && (
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>{document.description}</p>
                )}
                {isStandardDocument(document) && uploadStatus[document.id]?.uploaded && (
                  <span className="document-meta">
                    {[
                      uploadStatus[document.id].filename,
                      uploadStatus[document.id].uploadDate
                        ? new Date(uploadStatus[document.id].uploadDate).toLocaleDateString()
                        : null
                    ].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
              <div className="document-header-actions">
                <span className={`status-indicator ${uploadStatus[document.id]?.uploaded ? 'uploaded' : (document.isOptional ? 'optional' : 'missing')}`}>
                  {uploadStatus[document.id]?.uploaded ? 'Uploaded' : (document.isOptional ? 'Optional' : 'Required')}
                </span>
                {/* Uploaded standard documents keep their actions on this same row so
                    the card stays a single wide strip rather than a tall block. */}
                {isStandardDocument(document) && uploadStatus[document.id]?.uploaded && (
                  <div className="file-actions">
                    {uploadStatus[document.id].fileUrl && (
                      <>
                        {!isXlsxFile(uploadStatus[document.id].filename) && (
                          <button
                            className="view-btn"
                            onClick={() => window.open(uploadStatus[document.id].fileUrl, '_blank')}
                          >
                            View
                          </button>
                        )}
                        <button
                          className="download-btn"
                          onClick={() => handleFileDownload(uploadStatus[document.id].fileUrl, uploadStatus[document.id].filename)}
                        >
                          Download
                        </button>
                      </>
                    )}
                    <button
                      className="replace-btn"
                      onClick={() => triggerFileInput(document.id)}
                    >
                      Replace
                    </button>
                  </div>
                )}
                {document.id !== 12 && document.id !== ADDITIONAL_DOCUMENTS_DOC_ID && !uploadStatus[document.id]?.uploaded && (
                  <button
                    className="upload-btn-inline"
                    onClick={() => triggerFileInput(document.id)}
                    disabled={uploadStatus[document.id]?.uploading}
                    style={{
                      height: '28px',
                      padding: '0 12px',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: uploadStatus[document.id]?.uploading ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {uploadStatus[document.id]?.uploading
                      ? 'Uploading...'
                      : document.isOptional
                        ? 'Upload (Optional)'
                        : isBudgetDocument(document) ? 'Upload' : 'Upload (PDF)'}
                  </button>
                )}
              </div>
            </div>

            {document.id === ADDITIONAL_DOCUMENTS_DOC_ID ? (
              // Additional Documents: any number of files, each with a note
              <div className="biosketches-section">
                <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6b7280' }}>
                  {document.description}
                </p>

                {additionalDocuments.length > 0 && (
                  <div className="biosketches-list">
                    <h5>Uploaded Documents</h5>
                    {additionalDocuments.map(doc => (
                      <div key={doc.id} className="biosketch-item">
                        <div className="biosketch-info">
                          {editingAdditionalDocId === doc.id ? (
                            <textarea
                              className="note-editor"
                              value={editingAdditionalDocNotes}
                              onChange={(e) => setEditingAdditionalDocNotes(e.target.value)}
                              placeholder="Tell us what this document is"
                              rows={2}
                            />
                          ) : (
                            <span className="personnel-name">
                              {doc.notes || 'No note provided'}
                            </span>
                          )}
                          <span className="biosketch-meta">
                            {[
                              doc.filename.replace(/^Additional_\d+_/, ''),
                              doc.uploadDate ? new Date(doc.uploadDate).toLocaleDateString() : null
                            ].filter(Boolean).join(' · ')}
                          </span>
                        </div>
                        <div className="biosketch-actions">
                          {editingAdditionalDocId === doc.id ? (
                            <>
                              <button
                                className="view-btn small"
                                disabled={savingAdditionalDocNotes}
                                onClick={() => handleSaveAdditionalDocNotes(doc.id)}
                              >
                                {savingAdditionalDocNotes ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                className="replace-btn small"
                                disabled={savingAdditionalDocNotes}
                                onClick={() => {
                                  setEditingAdditionalDocId(null);
                                  setEditingAdditionalDocNotes('');
                                }}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="edit-btn small"
                                onClick={() => {
                                  setEditingAdditionalDocId(doc.id);
                                  setEditingAdditionalDocNotes(doc.notes || '');
                                }}
                              >
                                Edit Note
                              </button>
                              {doc.fileUrl && (
                                <>
                                  {!isXlsxFile(doc.filename) && (
                                    <button
                                      className="view-btn small"
                                      onClick={() => window.open(doc.fileUrl, '_blank')}
                                    >
                                      View
                                    </button>
                                  )}
                                  <button
                                    className="download-btn small"
                                    onClick={() => handleFileDownload(doc.fileUrl, doc.filename)}
                                  >
                                    Download
                                  </button>
                                </>
                              )}
                              <button
                                className="remove-btn small"
                                onClick={() => handleRemoveAdditionalDocument(doc.id)}
                              >
                                Remove
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* The note and the upload button are one action, so they're boxed
                    together — same layout as the subcontractor documents page. */}
                <div className="additional-doc-upload-box">
                  <div style={{ marginBottom: '10px' }}>
                    <label
                      htmlFor="additional-doc-notes"
                      style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}
                    >
                      Upload Name, Title, or Description:
                    </label>
                    <textarea
                      id="additional-doc-notes"
                      value={additionalDocNotes}
                      onChange={(e) => setAdditionalDocNotes(e.target.value)}
                      placeholder="Describe the document before adding the file"
                      rows={2}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="additional-doc-upload-row">
                    <span className="additional-doc-upload-label">Add a document</span>
                    <input
                      type="file"
                      ref={additionalDocFileInputRef}
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleAdditionalDocumentUpload(file, additionalDocNotes);
                      }}
                    />
                    <button
                      className="add-biosketch-btn"
                      disabled={additionalDocUploading}
                      onClick={() => additionalDocFileInputRef.current?.click()}
                    >
                      {additionalDocUploading ? 'Uploading...' : '+ Upload (Optional)'}
                    </button>
                  </div>
                </div>
              </div>
            ) : document.id === 12 ? (
              // Special UI for biosketches
              <div className="biosketches-section">
                {/* List of uploaded biosketches */}
                {biosketches.length > 0 && (
                  <div className="biosketches-list">
                    <h5>Uploaded Biosketches</h5>
                    {biosketches.map(biosketch => (
                      <div key={biosketch.id} className="biosketch-item">
                        <div className="biosketch-info">
                          <span className="personnel-name">
                            {biosketch.firstName} {biosketch.middleName ? `${biosketch.middleName} ` : ''}{biosketch.lastName}
                          </span>
                          <span className="position-title">{biosketch.positionTitle || 'No title provided'}</span>
                          <span className="organization">
                            {biosketch.organizationName || 'No organization provided'}
                          </span>
                          <span className="project-role">Role: {biosketch.projectRole || 'Not specified'}</span>
                          <span className="biosketch-detail">
                            {[
                              biosketch.street1,
                              biosketch.street2,
                              [biosketch.city, biosketch.state].filter(Boolean).join(', '),
                              biosketch.zipCode,
                              biosketch.country
                            ].filter(Boolean).join(' · ') || 'No address provided'}
                          </span>
                          <span className="biosketch-detail">
                            {[
                              biosketch.phoneNumber,
                              biosketch.email,
                              biosketch.eraCommonsUsername ? `eRA: ${biosketch.eraCommonsUsername}` : null
                            ].filter(Boolean).join(' · ') || 'No contact details provided'}
                          </span>
                          <span className="filename">{biosketch.filename.replace(/^Biosketch_.*_/, '')}</span>
                          {biosketch.uploadDate && (
                            <span className="upload-date">
                              Uploaded: {new Date(biosketch.uploadDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        
                        <div className="biosketch-actions">
                          {biosketch.uploading ? (
                            <span className="uploading-status">Uploading...</span>
                          ) : (
                            <>
                              <button
                                className="edit-btn small"
                                onClick={() => openEditBiosketchForm(biosketch)}
                              >
                                Edit
                              </button>
                              {biosketch.fileUrl && (
                                <>
                                  {!isXlsxFile(biosketch.filename) && (
                                    <button
                                      className="view-btn small"
                                      onClick={() => window.open(biosketch.fileUrl, '_blank')}
                                    >
                                      View
                                    </button>
                                  )}
                                  <button
                                    className="download-btn small"
                                    onClick={() => handleFileDownload(biosketch.fileUrl, biosketch.filename)}
                                  >
                                    Download
                                  </button>
                                </>
                              )}
                              <button 
                                className="remove-btn small"
                                onClick={() => handleRemoveBiosketch(biosketch.id)}
                              >
                                Remove
                              </button>
                            </>
                          )}
                        </div>
                        
                        {biosketch.error && (
                          <div className="biosketch-error">{biosketch.error}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* One biosketch is needed per senior/key person named in the
                    questionnaire; anyone still missing one is listed here. */}
                {keyPersonnelNeedingBiosketch.length > 0 && !showBiosketchForm && (
                  <div className="biosketches-list">
                    <h5>Biosketch still needed</h5>
                    {keyPersonnelNeedingBiosketch.map((person, index) => (
                      <div key={index} className="biosketch-item">
                        <div className="biosketch-info">
                          <span className="personnel-name">
                            {`${person.firstName || ''} ${person.lastName || ''}`.trim()}
                          </span>
                          <span className="biosketch-meta">
                            {[
                              person.projectRole,
                              person.organizationName
                            ].filter(Boolean).join(' \u00b7 ') || 'From the questionnaire'}
                          </span>
                        </div>
                        <div className="biosketch-actions">
                          <button
                            className="edit-btn small"
                            onClick={() => openBiosketchFormForKeyPerson(person)}
                          >
                            Add Biosketch
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Button and form to add more biosketches */}
                {showBiosketchForm ? (
                  <div className="biosketch-upload-form">
                    <div className="biosketch-header">
                      <h5>{editingBiosketchId ? 'Edit Personnel Biosketch' : 'Add Personnel Biosketch'}</h5>
                      {(() => {
                        console.log('savedPersonnel.length:', savedPersonnel.length, 'savedPersonnel:', savedPersonnel);
                        return savedPersonnel.length > 0;
                      })() && (
                        <button 
                          type="button" 
                          className="autofill-personnel-btn"
                          onClick={() => setShowPersonnelAutofill(!showPersonnelAutofill)}
                        >
                          Auto-fill from Previous Grant
                        </button>
                      )}
                    </div>

                    {showPersonnelAutofill && savedPersonnel.length > 0 && (
                      <div className="personnel-autofill-dropdown">
                        <p>Select personnel to auto-fill contact information (biosketch will still need to be uploaded separately):</p>
                        {savedPersonnel.map((person, index) => (
                          <button
                            key={index}
                            type="button"
                            className="personnel-autofill-option"
                            onClick={() => handlePersonnelAutofill(person)}
                          >
                            <div className="person-name">{person.firstName} {person.lastName}</div>
                            <div className="person-details">{person.positionTitle} - {person.organizationName}</div>
                          </button>
                        ))}
                        <button 
                          type="button" 
                          className="cancel-personnel-autofill"
                          onClick={() => setShowPersonnelAutofill(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    
                    <div className="form-row">
                      <div className="form-group required">
                        <label htmlFor="firstName">First Name:</label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={biosketchFormData.firstName}
                          onChange={handleBiosketchFormChange}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="middleName">Middle Name:</label>
                        <input
                          type="text"
                          id="middleName"
                          name="middleName"
                          value={biosketchFormData.middleName}
                          onChange={handleBiosketchFormChange}
                        />
                      </div>
                      
                      <div className="form-group required">
                        <label htmlFor="lastName">Last Name:</label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={biosketchFormData.lastName}
                          onChange={handleBiosketchFormChange}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group required">
                        <label htmlFor="positionTitle">Position/Title:</label>
                        <input
                          type="text"
                          id="positionTitle"
                          name="positionTitle"
                          value={biosketchFormData.positionTitle}
                          onChange={handleBiosketchFormChange}
                          required
                        />
                      </div>
                      
                      <div className="form-group required">
                        <label htmlFor="organizationName">Organization Name:</label>
                        <input
                          type="text"
                          id="organizationName"
                          name="organizationName"
                          value={biosketchFormData.organizationName}
                          onChange={handleBiosketchFormChange}
                          required
                        />
                      </div>

                    </div>
                    
                    <div className="address-section">
                      <h6>Business Address</h6>
                      
                      <div className="form-group required">
                        <label htmlFor="street1">Street 1:</label>
                        <input
                          type="text"
                          id="street1"
                          name="street1"
                          value={biosketchFormData.street1}
                          onChange={handleBiosketchFormChange}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="street2">Street 2:</label>
                        <input
                          type="text"
                          id="street2"
                          name="street2"
                          value={biosketchFormData.street2}
                          onChange={handleBiosketchFormChange}
                        />
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group required">
                          <label htmlFor="city">City:</label>
                          <input
                            type="text"
                            id="city"
                            name="city"
                            value={biosketchFormData.city}
                            onChange={handleBiosketchFormChange}
                            required
                          />
                        </div>
                        
                        <div className="form-group required">
                          <label htmlFor="state">State:</label>
                          <input
                            type="text"
                            id="state"
                            name="state"
                            value={biosketchFormData.state}
                            onChange={handleBiosketchFormChange}
                            required
                          />
                        </div>
                        
                        <div className="form-group required">
                          <label htmlFor="zipCode">Zip/Postal Code:</label>
                          <input
                            type="text"
                            id="zipCode"
                            name="zipCode"
                            value={biosketchFormData.zipCode}
                            onChange={handleBiosketchFormChange}
                            required
                          />
                        </div>
                        
                        <div className="form-group required">
                          <label htmlFor="country">Country:</label>
                          <input
                            type="text"
                            id="country"
                            name="country"
                            value={biosketchFormData.country}
                            onChange={handleBiosketchFormChange}
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group required">
                        <label htmlFor="phoneNumber">Phone Number:</label>
                        <input
                          type="tel"
                          id="phoneNumber"
                          name="phoneNumber"
                          value={biosketchFormData.phoneNumber}
                          onChange={handleBiosketchFormChange}
                          pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                          required
                        />
                      </div>
                      
                      <div className="form-group required">
                        <label htmlFor="email">Email:</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={biosketchFormData.email}
                          onChange={handleBiosketchFormChange}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="eraCommonsUsername">eRA Commons Username:</label>
                        <input
                          type="text"
                          id="eraCommonsUsername"
                          name="eraCommonsUsername"
                          value={biosketchFormData.eraCommonsUsername}
                          onChange={handleBiosketchFormChange}
                        />
                      </div>
                      
                      <div className="form-group required">
                        <label htmlFor="projectRole">Project Role:</label>
                        <select
                          id="projectRole"
                          name="projectRole"
                          value={biosketchFormData.projectRole}
                          onChange={handleBiosketchFormChange}
                          required
                        >
                          <option value="">-- Select --</option>
                          {PROJECT_ROLES.map(role => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    
                    {editingBiosketchId ? (
                      <div className="form-group biosketch-file">
                        <label>Biosketch File:</label>
                        <span className="file-name">
                          {biosketches.find(bs => bs.id === editingBiosketchId)?.filename || 'Already uploaded'}
                        </span>
                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                          To swap the file itself, remove this biosketch and upload it again.
                        </p>
                      </div>
                    ) : (
                      <div className="form-group biosketch-file required">
                        <label htmlFor="biosketch-file-upload">Biosketch File:</label>
                        <div className="file-upload-button">
                          <button 
                            className="upload-file-btn"
                            onClick={() => biosketchFileInputRef.current?.click()}
                          >
                            Select File
                          </button>
                          <span className="file-name">
                            {biosketchFileInputRef.current?.files?.[0]?.name || "No file selected"}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    <div className="biosketch-form-actions">
                      <button 
                        className="submit-btn"
                        onClick={handleBiosketchSubmit}
                        disabled={
                          biosketchUploading ||
                          !biosketchFormData.firstName ||
                          !biosketchFormData.lastName ||
                          (!editingBiosketchId && !biosketchFileInputRef.current?.files?.[0])
                        }
                      >
                        {editingBiosketchId
                          ? (biosketchUploading ? 'Saving...' : 'Save Changes')
                          : (biosketchUploading ? 'Uploading...' : 'Upload Biosketch')}
                      </button>
                      <button 
                        className="cancel-btn"
                        onClick={closeBiosketchForm}
                      >
                        Cancel
                      </button>
                    </div>
                    
                    <input
                      type="file"
                      id="biosketch-file-upload"
                      ref={biosketchFileInputRef}
                      onChange={(e) => {
                        // Just to update the UI to show the file name
                        setBiosketchFormData(prev => ({ ...prev }));
                      }}
                      style={{ display: 'none' }}
                    />
                  </div>
                ) : (
                  <button 
                    className="add-biosketch-btn"
                    onClick={openNewBiosketchForm}
                  >
                    + Add Personnel Biosketch
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Descriptions aren't shown for the standard documents, but the optional
                    ones carry instructions the client needs to see. */}
                {document.isOptional && document.description && (
                  <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6b7280' }}>
                    {document.description}
                  </p>
                )}

                {document.hasExample && (
                  <button 
                    className="example-btn"
                    onClick={() => handleDownloadExample(document.id)}
                  >
                    Download Template
                  </button>
                )}

                <input
                  type="file"
                  id={`file-upload-${document.id}`}
                  ref={el => fileInputRefs.current[document.id] = el}
                  onChange={(e) => handleFileChange(e, document.id)}
                  style={{ display: 'none' }}
                />
                
                {uploadStatus[document.id]?.error && (
                  <div className="upload-error">{uploadStatus[document.id].error}</div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default GrantDocuments;